<template>
  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
    <!-- KPI 1: Ventas Totales -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-5 relative overflow-hidden transition-all hover:shadow-md">
      <div class="flex items-center justify-between mb-3">
        <span class="text-xs font-bold uppercase tracking-wider text-gray-500">
          Ventas Totales
        </span>
        <div class="w-9 h-9 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
          <i class="fa fa-shopping-bag text-base" />
        </div>
      </div>
      <div class="text-2xl font-black text-gray-900 tracking-tight">
        {{ formatCurrency(stats.total_sales) }}
      </div>
      <div class="mt-2 flex items-center justify-between text-xs text-gray-500">
        <span>{{ stats.total_orders }} órdenes</span>
        <span class="font-medium text-gray-700">Ticket: {{ formatCurrency(stats.avg_ticket) }}</span>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-50 flex items-center justify-between text-[11px]">
        <span class="text-emerald-600 font-semibold">
          <i class="fa fa-check-circle" /> Cobrado: {{ formatCurrency(stats.total_sales_collected) }}
        </span>
        <span v-if="stats.total_sales_pending > 0" class="text-amber-600 font-semibold">
          Por cobrar: {{ formatCurrency(stats.total_sales_pending) }}
        </span>
      </div>
    </div>

    <!-- KPI 2: Compras a Proveedores -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-5 relative overflow-hidden transition-all hover:shadow-md">
      <div class="flex items-center justify-between mb-3">
        <span class="text-xs font-bold uppercase tracking-wider text-gray-500">
          Compras Proveedores
        </span>
        <div class="w-9 h-9 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
          <i class="fa fa-truck-loading text-base" />
        </div>
      </div>
      <div class="text-2xl font-black text-gray-900 tracking-tight">
        {{ formatCurrency(stats.total_purchases) }}
      </div>
      <div class="mt-2 flex items-center justify-between text-xs text-gray-500">
        <span>{{ stats.total_shoppings_count }} compras</span>
        <span class="font-medium text-purple-700">Pagado: {{ formatCurrency(stats.total_purchases_paid) }}</span>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-50 flex items-center justify-between text-[11px]">
        <span class="text-gray-500">
          Inversión de inventario
        </span>
        <span v-if="stats.total_purchases_pending > 0" class="text-rose-600 font-semibold">
          Por pagar: {{ formatCurrency(stats.total_purchases_pending) }}
        </span>
        <span v-else class="text-emerald-600 font-semibold">Al día</span>
      </div>
    </div>

    <!-- KPI 3: Ganancia Bruta -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-5 relative overflow-hidden transition-all hover:shadow-md">
      <div class="flex items-center justify-between mb-3">
        <span class="text-xs font-bold uppercase tracking-wider text-gray-500">
          Ganancia Bruta
        </span>
        <div class="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
          <i class="fa fa-chart-line text-base" />
        </div>
      </div>
      <div class="text-2xl font-black text-emerald-600 tracking-tight">
        {{ formatCurrency(stats.gross_profit) }}
      </div>
      <div class="mt-2 flex items-center justify-between text-xs text-gray-500">
        <span>Margen Bruto:</span>
        <span class="font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
          {{ stats.gross_margin_pct }}%
        </span>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-50 text-[11px] text-gray-400">
        Costo mercancía vendida: {{ formatCurrency(stats.total_cogs) }}
      </div>
    </div>

    <!-- KPI 4: Utilidad Neta (P&L) -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-5 relative overflow-hidden transition-all hover:shadow-md">
      <div class="flex items-center justify-between mb-3">
        <span class="text-xs font-bold uppercase tracking-wider text-gray-500">
          Utilidad Neta (P&L)
        </span>
        <div
          class="w-9 h-9 rounded-lg flex items-center justify-center"
          :class="stats.net_profit >= 0 ? 'bg-blue-50 text-blue-600' : 'bg-red-50 text-red-600'"
        >
          <i class="fa fa-balance-scale text-base" />
        </div>
      </div>
      <div
        class="text-2xl font-black tracking-tight"
        :class="stats.net_profit >= 0 ? 'text-blue-700' : 'text-red-600'"
      >
        {{ formatCurrency(stats.net_profit) }}
      </div>
      <div class="mt-2 flex items-center justify-between text-xs text-gray-500">
        <span>Margen Neto:</span>
        <span
          class="font-bold px-2 py-0.5 rounded-full"
          :class="stats.net_profit >= 0 ? 'text-blue-800 bg-blue-50' : 'text-red-800 bg-red-50'"
        >
          {{ stats.net_margin_pct }}%
        </span>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-50 flex items-center justify-between text-[11px] text-gray-500">
        <span>Gastos op.: {{ formatCurrency(stats.total_expenses) }}</span>
        <span class="text-gray-400 font-mono">Caja: {{ formatCurrency(stats.cash_flow_balance) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReportKpiCards',

  props: {
    stats: {
      type: Object,
      required: true,
    },
  },

  methods: {
    formatCurrency(val) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(val || 0)
    },
  },
}
</script>
